import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AlertConditionCard } from '../../../models/api/alerts/alert-condition-card';
import { CasePipe } from '../../../pipes/case.pipe';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { BaseComponent } from '../../base/base.component';
import { TagComponent } from '../../core/labels/tag.component';

@Component({
  selector: 'app-alert-condition',
  imports: [CasePipe, HighlightPipe, CommonModule, TagComponent],
  template: `
    <div
      class="alert-condition-card tw-relative tw-border tw-rounded-xl tw-flex tw-flex-col tw-cursor-pointer hover:tw-shadow-lg tw-h-full"
      tabindex="-1"
      (click)="onSelect()"
      (keydown.enter)="onEnter($event)"
      (keydown.space)="onSelect($event)"
      [ngClass]="{
        'tw-border-4 tw-border-[var(--sed-engaged-black-color)]': isSelected,
        'tw-border': !isSelected,
      }"
    >
      <!-- Radio must not wrap body content so title/desc/LOBs stay in browse reading order.
           pointer-events-none keeps description scroll / click on the card.
           Tab stop stays on this radio (tabindex 0); card uses tabindex -1 only for eslint. -->
      <div
        role="radio"
        [attr.aria-checked]="isSelected"
        [attr.aria-labelledby]="accessibleNameIds"
        tabindex="0"
        class="tw-absolute tw-inset-0 tw-rounded-xl tw-pointer-events-none"
        (keydown.enter)="onEnter($event)"
        (keydown.space)="onSelect($event)"
      ></div>
      <div
        [id]="titleDomId"
        class="tw-font-bold tw-p-2 tw-text-balance tw-rounded-t-xl tw-bg-[var(--sed-open-grey-color)]"
        [innerHTML]="
          condition.title
            | case: $any(condition.labelCase) ?? 'sentence'
            | highlight: filterValue
        "
      ></div>
      <div [id]="descDomId" class="tw-my-3 tw-px-3 tw-text-sm tw-grow">
        <div class="tw-overflow-y-auto tw-max-h-[150px]">
          {{ condition.description | case: condition.labelCase ?? 'sentence' }}
        </div>
      </div>
      <span [id]="extraDescDomId" class="tw-sr-only">
        @if (condition.favorite) { Popular. }
        @if (condition.lobs?.length) {
          Lines of business: {{ lobsAccessibleText }}
        }
      </span>
      <div class="tw-flex tw-gap-2 tw-justify-between tw-items-center tw-m-2" aria-hidden="true">
        @if (condition.favorite) {
          <app-tag type="Purple"
            ><span class="tw-text-[12px]">Popular</span></app-tag
          >
        } @else {
          <span></span>
        }

        <ul class="tw-flex tw-gap-2 tw-flex-wrap tw-list-none tw-m-0 tw-p-0">
          @for (lob of condition.lobs; track lob) {
            <li class="lob tw-relative tw-rounded-full tw-h-6 tw-w-8 tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-leading-none">
              <abbr [title]="getLobFullName(lob)" class="tw-no-underline">{{ lob }}</abbr>
            </li>
          }
        </ul>
      </div>
    </div>
  `,
  styles: `
    .lob {
      color: var(--sed-black-80-color);
      background-color: var(--sed-grey-60-color);
      font-weight: bold;
    }

    .alert-condition-card:has([role='radio']:focus-visible) {
      outline: 2px solid;
      outline-offset: 3px;
    }

    .alert-condition-card [role='radio']:focus-visible {
      outline: none;
    }
  `,
})
export class AlertConditionComponent extends BaseComponent {
  private static readonly lobLabels: Record<string, string> = {
    WC: "Workers' Compensation",
    DS: 'Disability',
    LA: 'Leave',
    AU: 'Auto Liability',
    GL: 'General Liability',
    PR: 'Property',
  };

  @Input()
  condition: AlertConditionCard;

  @Input()
  control: FormControl<AlertConditionCard>;

  @Input()
  filterValue: string;

  @Output()
  doubleEnter = new EventEmitter<void>();

  private lastEnterTime = 0;
  private readonly doubleEnterThreshold = 400;

  get titleDomId(): string {
    return `alert-condition-title-${this.condition.type}`;
  }

  get descDomId(): string {
    return `alert-condition-desc-${this.condition.type}`;
  }

  get extraDescDomId(): string {
    return `alert-condition-extra-${this.condition.type}`;
  }

  get accessibleNameIds(): string {
    return `${this.titleDomId} ${this.descDomId} ${this.extraDescDomId}`;
  }

  get lobsAccessibleText(): string {
    if (!this.condition.lobs?.length) return '';
    return this.condition.lobs.map((lob) => this.getLobFullName(lob)).join(', ');
  }

  get isSelected(): boolean {
    return this.control.value?.type === this.condition.type;
  }

  getLobFullName(lob: string): string {
    return AlertConditionComponent.lobLabels[lob?.toUpperCase()] ?? lob;
  }

  onSelect(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.control.setValue(this.condition);
  }

  onEnter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const now = Date.now();
    if (now - this.lastEnterTime < this.doubleEnterThreshold) {
      this.doubleEnter.emit();
      this.lastEnterTime = 0;
    } else {
      this.onSelect();
      this.lastEnterTime = now;
    }
  }
}
